import { Instant, InstantSchema } from "@/Temporal"
import { enumSchema, mapApiType, nullableSchema, objectSchema, type ApiTypeOf } from "vue-utils"
import { CallInformation } from "./CallInformation"
import { DocumentUpload } from "./DocumentUpload"
import { EmergencyPhase } from "./EmergencyPhase"
import type { Id, IncidentEntity, Nullable } from "./IncidentEntity"
import { IncidentLocation } from "./IncidentLocation"
import { IncidentStatus } from "./IncidentStatus"
import { NoteEntry } from "./NoteEntry"
import { UserTimeStamp } from "./UserTimeStamp"
import { Tide, WeatherConditions } from "./conditions"
import { ShipDescription, ShipRegistration } from "./ship"
import { AssetTasked } from "./sru"

export interface IncidentLog extends IncidentEntity {
	identifier: string
	incidentTime: Instant
	createdBy: UserTimeStamp

	status: IncidentStatus

	SMCOnDutyId: Nullable<Id>

	primaryMWOId: Nullable<Id>
	secondaryMWOId: Nullable<Id>

	reportedIncidentTypeId: Id
	finalIncidentTypeId: Nullable<Id>

	initialEmergencyPhase: EmergencyPhase
	finalEmergencyPhase: Nullable<EmergencyPhase>

	nameOfVesselOrCasualty: Nullable<string>
	personsAtRisk: number

	positions: IncidentLocation[]

	messageReceived: string
	natureOfIncident: Nullable<string>
	descriptionOfCasualty: Nullable<string>

	shipDescription: ShipDescription
	assetsTasked: AssetTasked[]
	callReceived: CallInformation
	registrationInformation: ShipRegistration
	weatherConditions: WeatherConditions
	currentTide: Tide

	incidentNarrative: NoteEntry[]
	incidentDocuments: DocumentUpload[]

	reopened: Nullable<UserTimeStamp>
	closed: Nullable<UserTimeStamp>
	signedOff: Nullable<UserTimeStamp>
}

export const IncidentLog = objectSchema<IncidentLog>({
	id: Number,
	identifier: String,
	incidentTime: InstantSchema,
	createdBy: UserTimeStamp,

	status: enumSchema(IncidentStatus),

	SMCOnDutyId: nullableSchema(Number),

	primaryMWOId: nullableSchema(Number),
	secondaryMWOId: nullableSchema(Number),

	reportedIncidentTypeId: Number,
	finalIncidentTypeId: nullableSchema(Number),

	initialEmergencyPhase: enumSchema(EmergencyPhase),
	finalEmergencyPhase: nullableSchema(enumSchema(EmergencyPhase)),

	nameOfVesselOrCasualty: nullableSchema(String),
	personsAtRisk: Number,

	positions: IncidentLocation.array(),

	messageReceived: String,
	natureOfIncident: nullableSchema(String),
	descriptionOfCasualty: nullableSchema(String),

	shipDescription: ShipDescription,
	assetsTasked: AssetTasked.array(),
	callReceived: CallInformation,
	registrationInformation: ShipRegistration,
	weatherConditions: WeatherConditions,
	currentTide: Tide,

	incidentNarrative: NoteEntry.array(),
	incidentDocuments: DocumentUpload.array(),

	reopened: nullableSchema(UserTimeStamp),
	closed: nullableSchema(UserTimeStamp),
	signedOff: nullableSchema(UserTimeStamp),
})

export function mapApiIncidentLog(log: ApiTypeOf<IncidentLog>): IncidentLog {
	return mapApiType(IncidentLog, log)
}
